import React from "react";
import { Modal } from "antd";
import {
  EditOutlined,
  CalendarOutlined,
  ShopOutlined,
  TableOutlined,
  TagOutlined,
  FieldTimeOutlined,
  DollarCircleOutlined,
  CheckCircleFilled,
} from "@ant-design/icons";

// Same field-name assumptions as PlanCard.jsx — adjust the getters below
// if your real plan object uses different keys.

const ModalStyles = () => (
  <style>{`
    .plan-modal-v2 .ant-modal-content {
      padding: 0;
      border-radius: 16px;
      overflow: hidden;
    }
    .plan-modal-v2 .ant-modal-close {
      top: 14px;
      right: 14px;
      color: rgba(255,255,255,0.85);
    }
    .plan-modal-v2 .ant-modal-close:hover {
      color: #fff;
    }
    @keyframes planStatIn {
      from { opacity: 0; transform: translateY(4px); }
      to { opacity: 1; transform: translateY(0); }
    }
    .plan-stat-tile {
      animation: planStatIn 0.35s ease-out both;
    }
    .plan-stat-tile:nth-child(1) { animation-delay: 0.02s; }
    .plan-stat-tile:nth-child(2) { animation-delay: 0.06s; }
    .plan-stat-tile:nth-child(3) { animation-delay: 0.10s; }
    .plan-stat-tile:nth-child(4) { animation-delay: 0.14s; }
    .plan-stat-tile:nth-child(5) { animation-delay: 0.18s; }
    .plan-stat-tile:nth-child(6) { animation-delay: 0.22s; }
    @keyframes planBannerShimmer {
      0% { background-position: -150% 0; }
      100% { background-position: 250% 0; }
    }
    .plan-banner-shimmer::after {
      content: "";
      position: absolute;
      inset: 0;
      background: linear-gradient(100deg, transparent 30%, rgba(255,255,255,0.16) 48%, rgba(255,255,255,0.16) 52%, transparent 70%);
      background-size: 250% 100%;
      animation: planBannerShimmer 4s ease-in-out infinite;
      pointer-events: none;
    }
  `}</style>
);

const StatTile = ({ icon, label, value }) => (
  <div className="plan-stat-tile flex items-start gap-2.5 bg-gray-50 border border-gray-100 rounded-xl p-3">
    <div className="w-8 h-8 rounded-lg bg-white border border-gray-200 flex items-center justify-center text-indigo-500 shrink-0">
      {icon}
    </div>
    <div className="min-w-0">
      <div className="text-[11px] text-gray-400 leading-none">{label}</div>
      <div className="text-sm font-semibold text-gray-800 mt-1 truncate">{value}</div>
    </div>
  </div>
);

const PlanDetailsModal = ({ open, plan, onClose, onEdit }) => {
  if (!plan) return null;

  const name = plan?.name || plan?.title || "Untitled Plan";
  const description = plan?.description || "No description provided for this plan.";
  const isActive =
    typeof plan?.isActive === "boolean" ? plan.isActive : plan?.status === "active";
  const planType = (plan?.planType || (Number(plan?.price) === 0 ? "free" : "paid")).toLowerCase();
  const isFree = planType === "free";
  const price = Number(plan?.price ?? 0);
  const durationDays = plan?.durationDays ?? plan?.duration ?? "-";
  const maxRestaurants = plan?.maxRestaurants ?? "-";
  const maxTables = plan?.maxTables ?? "-";
  const features = Array.isArray(plan?.features) ? plan.features : [];
  const createdLabel = plan?.createdAt
    ? new Date(plan.createdAt).toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      })
    : "-";

  const banner = isFree
    ? "linear-gradient(135deg,#64748b,#334155)"
    : "linear-gradient(135deg,#6366f1,#7c3aed)";

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      width={620}
      centered
      className="plan-modal-v2"
      destroyOnClose
    >
      <ModalStyles />

      {/* Banner header */}
      <div
        className="plan-banner-shimmer relative px-6 pt-7 pb-6 text-white"
        style={{ background: banner }}
      >
        <div className="flex items-center gap-2 mb-2">
          <span className="text-[11px] uppercase tracking-wide font-semibold bg-white/15 px-2 py-0.5 rounded-full border border-white/20">
            {isFree ? "Free Plan" : "Paid Plan"}
          </span>
          <span
            className={`inline-flex items-center gap-1 text-[11px] font-medium px-2 py-0.5 rounded-full ${
              isActive ? "bg-emerald-400/20 text-emerald-100" : "bg-white/10 text-white/70"
            }`}
          >
            <CheckCircleFilled style={{ fontSize: 10 }} />
            {isActive ? "Active" : "Inactive"}
          </span>
        </div>
        <h2 className="text-2xl font-bold leading-tight">{name}</h2>
        <p className="text-sm text-white/75 mt-1.5 max-w-md">{description}</p>

        <div className="mt-4 flex items-baseline gap-1.5">
          <span className="text-3xl font-bold tabular-nums">
            {price === 0 ? "Free" : `₹${price.toLocaleString("en-IN")}`}
          </span>
          <span className="text-sm text-white/70">
            {price === 0 ? `for ${durationDays} days` : `/ ${durationDays} days`}
          </span>
        </div>
      </div>

      {/* Body */}
      <div className="px-6 py-5">
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          <StatTile
            icon={<DollarCircleOutlined />}
            label="Price"
            value={price === 0 ? "Free" : `₹${price.toLocaleString("en-IN")}`}
          />
          <StatTile icon={<FieldTimeOutlined />} label="Duration" value={`${durationDays} days`} />
          <StatTile icon={<TagOutlined />} label="Plan Type" value={isFree ? "Free" : "Paid"} />
          <StatTile icon={<ShopOutlined />} label="Max Restaurants" value={maxRestaurants} />
          <StatTile icon={<TableOutlined />} label="Max Tables" value={maxTables} />
          <StatTile icon={<CalendarOutlined />} label="Created On" value={createdLabel} />
        </div>

        <div className="mt-5">
          <h4 className="text-sm font-semibold text-gray-800 mb-2">
            Features included {features.length > 0 && `(${features.length})`}
          </h4>
          {features.length > 0 ? (
            <div className="flex flex-wrap gap-2">
              {features.map((f, i) => (
                <span
                  key={i}
                  className="inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-lg bg-indigo-50 text-indigo-600 border border-indigo-100"
                >
                  <CheckCircleFilled style={{ fontSize: 10 }} />
                  {f}
                </span>
              ))}
            </div>
          ) : (
            <p className="text-xs text-gray-400">No features listed for this plan.</p>
          )}
        </div>
      </div>

      {/* Footer */}
      <div className="px-6 py-4 border-t border-gray-100 flex justify-end gap-2 bg-gray-50">
        <button
          onClick={onClose}
          className="px-4 py-2 rounded-lg text-sm font-medium text-gray-600 hover:bg-gray-100 transition"
        >
          Close
        </button>
        <button
          onClick={() => {
            onEdit?.(plan);
            onClose?.();
          }}
          className="px-4 py-2 rounded-lg text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 transition flex items-center gap-1.5"
        >
          <EditOutlined style={{ fontSize: 12 }} />
          Edit Plan
        </button>
      </div>
    </Modal>
  );
};

export default PlanDetailsModal;